import React, { useMemo } from "react";
import { Progress } from "antd";
import "./PortfolioHealthScore.css";
import "../../../../styles/dashboardCardTokens.css";

function normalizePortfolioHealth(apiData) {
  if (!apiData) return null;
  const ph = apiData.portfolio_health;
  const score =
    (typeof ph === "number" ? ph : null) ??
    (ph && typeof ph === "object" && typeof ph.score === "number" ? ph.score : null) ??
    apiData.portfolio_health_score ??
    apiData.score ??
    apiData.health_score ??
    apiData.portfolio_score;
  const buckets = apiData.bucket_counts;
  const elite =
    buckets?.ELITE?.count ??
    (ph && typeof ph === "object" ? ph.elite : null) ??
    apiData.elite_count ??
    apiData.eliteCount;
  const strong = buckets?.STRONG?.count ?? apiData.strong_count ?? apiData.strongCount ?? 0;
  const watch = buckets?.WATCH?.count ?? apiData.watch_count ?? apiData.watchCount ?? 0;
  const risk =
    buckets?.RISK?.count ??
    (ph && typeof ph === "object" ? ph.risk : null) ??
    apiData.risk_count ??
    apiData.riskCount;
  if (score == null && elite == null && risk == null) return null;
  const s = Number(score);
  return {
    score: Math.max(0, Math.min(100, isNaN(s) ? 0 : Math.round(s))),
    elite: Math.max(0, parseInt(elite, 10) || 0),
    strong: Math.max(0, parseInt(strong, 10) || 0),
    watch: Math.max(0, parseInt(watch, 10) || 0),
    risk: Math.max(0, parseInt(risk, 10) || 0),
  };
}

export default function PortfolioHealthScore({ vehicles = [], portfolioHealth: portfolioHealthApi }) {
  const healthData = useMemo(() => {
    const fromApi = normalizePortfolioHealth(portfolioHealthApi);
    if (fromApi) return fromApi;

    // Fallback: compute from vehicles
    const elite = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom <= 7 && delta < -5;
    });
    const strong = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom <= 7 && Math.abs(delta) < 5;
    });
    const watch = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom > 7 && dom <= 30 && Math.abs(delta) < 10;
    });
    const risk = vehicles.filter(v => {
      const dom = v.dom || 0;
      const delta = v.market_avg_delta || 0;
      return dom > 30 || delta > 10;
    });
    const totalVehicles = vehicles.length || 1;
    const eliteScore = (elite.length / totalVehicles) * 50;
    const riskPenalty = (risk.length / totalVehicles) * 30;
    const baseScore = 50;
    const computedScore = Math.max(0, Math.min(100, Math.round(baseScore + eliteScore - riskPenalty)));
    return {
      score: computedScore,
      elite: elite.length,
      strong: strong.length,
      watch: watch.length,
      risk: risk.length,
    };
  }, [vehicles, portfolioHealthApi]);

  const score = healthData.score;
  const greenShare = 0.35;
  const purpleShare = 0.65;

  return (
    <div className="portfolio-health-score-card dashboard-card-base">
      <div className="portfolio-health-left">
        <div className="portfolio-health-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="6" width="12" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.8"/>
            <rect x="6" y="4" width="12" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
            <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
            <line x1="8" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
          </svg>
        </div>
        <div className="portfolio-health-content">
          <div className="portfolio-health-title-row">
            <span className="portfolio-health-title">Portfolio Health Score</span>
            <span className="portfolio-health-score-value">{healthData.score} / 100</span>
          </div>
          {/* Mobile-only: "Portfolio Health" then number /100 (no "Score") */}
          <div className="portfolio-health-mobile-labels">
            <span className="ph-mobile-label">Portfolio Health</span>
            <span className="ph-mobile-value">{score}/100</span>
          </div>
          <div className="portfolio-health-gradient-bar"></div>
          {/* Mobile: custom two-tone progress bar (green + purple) */}
          <div className="portfolio-health-bar-mobile" role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100}>
            <div className="ph-bar-track">
              <div className="ph-bar-fill ph-bar-fill-green" style={{ width: `${score * greenShare}%` }} />
              <div className="ph-bar-fill ph-bar-fill-purple" style={{ width: `${score * purpleShare}%` }} />
            </div>
          </div>
          <div className="portfolio-health-breakdown">
            {healthData.elite} Elite • {healthData.strong} Strong • {healthData.watch} Watch • {healthData.risk} Risk
          </div>
        </div>
      </div>
      <div className="portfolio-health-right">
        <div className="portfolio-health-progress-container">
          <Progress
            percent={healthData.score}
            showInfo={false}
            strokeColor="#38DA9C"
            trailColor="rgba(255, 255, 255, 0.08)"
            className="portfolio-health-progress-bar"
          />
        </div>
        <div className="portfolio-health-progress-value">{healthData.score} / 100</div>
      </div>
    </div>
  );
}
